/**
 * SavedMappingsTab — Phase R10.4c (2026-07-05)
 *
 * Lists saved mappings for the owner's brand. Search + status filter.
 * Each row supports Edit (opens AssignBranchModal, mode=edit) and
 * Deactivate/Activate (status toggle).
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Loader2, Edit2, Power, PowerOff, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/auth';
import { SectionCard, QueryError } from '@/components/ui_primitives/Misc';
import AssignBranchModal from './AssignBranchModal';
import ApplyToExistingModal from './ApplyToExistingModal';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('id-ID', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

function StatusPill({ status }) {
  const cls =
    status === 'active'
      ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-300'
      : 'border-white/15 bg-white/5 text-white/55';
  const label = status === 'active' ? 'Active' : 'Inactive';
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${cls}`}
      data-testid={`outlet-mapping-status-${status}`}
    >
      {label}
    </span>
  );
}

export default function SavedMappingsTab({ onViewHistory }) {
  const token = useAuthStore((s) => s.token);
  const qc    = useQueryClient();
  const [search, setSearch]       = useState('');
  const [status, setStatus]       = useState('all');
  const [editing, setEditing]     = useState(null);
  const [applying, setApplying]   = useState(null);

  const mappingsQ = useQuery({
    queryKey: ['owner-outlet-mappings-list', { status, search }],
    queryFn:  () => api.ownerListOutletMappings(token, { status, search }),
    enabled:  !!token,
    staleTime: 15_000,
  });

  const branchesQ = useQuery({
    queryKey: ['owner-branches-for-mapping'],
    queryFn:  () => api.ownerListBranches(token),
    enabled:  !!token,
    staleTime: 60_000,
  });

  const patchMut = useMutation({
    mutationFn: ({ mappingId, patch }) =>
      api.ownerPatchOutletMapping(token, mappingId, patch),
    onSuccess: (res) => {
      toast.success(
        res?.status === 'inactive'
          ? 'Mapping deactivated — no new auto-resolutions.'
          : 'Mapping updated.',
      );
      setEditing(null);
      qc.invalidateQueries({ queryKey: ['owner-outlet-mappings-list'] });
      qc.invalidateQueries({ queryKey: ['owner-outlet-mappings-unmapped'] });
    },
    onError: (e) => {
      toast.error(e?.detail?.message ?? e?.detail ?? 'Could not update the mapping');
    },
  });

  const rows     = useMemo(() => mappingsQ.data?.items ?? [], [mappingsQ.data]);
  const branches = Array.isArray(branchesQ.data) ? branchesQ.data : [];
  const branchName = (id) => branches.find((b) => b.id === id)?.name ?? '—';

  return (
    <SectionCard>
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-display font-semibold text-white">
              Saved Mappings
            </h3>
            <p className="text-sm text-white/55 mt-1">
              Edit, deactivate, or review the history of each saved mapping.
            </p>
          </div>
          <div className="text-xs text-white/50" data-testid="outlet-mapping-saved-total">
            {rows.length} mapping{rows.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search size={14} className="text-white/40" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search outlet name…"
              data-testid="outlet-mapping-search"
              className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-1.5 text-sm text-white placeholder-white/35 focus:border-gold/60 focus:outline-none"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="outlet-mapping-status-filter"
            className="rounded-xl bg-white/5 border border-white/10 px-3 py-1.5 text-sm text-white focus:border-gold/60 focus:outline-none"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {mappingsQ.isLoading ? (
        <div className="py-10 flex items-center justify-center gap-2 text-white/55 text-sm">
          <Loader2 size={16} className="animate-spin text-gold" />
          Loading mappings…
        </div>
      ) : mappingsQ.isError ? (
        <QueryError error={mappingsQ.error} onRetry={mappingsQ.refetch} />
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-white/50 border border-dashed border-white/10 rounded-2xl">
          No saved mappings yet.
        </div>
      ) : (
        <div className="space-y-2" data-testid="outlet-mapping-saved-rows">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-center justify-between gap-4 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3"
              data-testid={`outlet-mapping-row-${row.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-white truncate">
                    {row.original_detected_outlet}
                  </span>
                  <StatusPill status={row.status} />
                </div>
                <div className="text-[11px] text-white/50 font-mono mt-0.5">
                  → {branchName(row.mapped_branch_id)}  ·  updated {fmtDate(row.updated_at)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => row.status === 'active' && setApplying(row)}
                  disabled={row.status !== 'active' || patchMut.isPending}
                  data-testid={`outlet-mapping-apply-${row.id}`}
                  className="p-2 rounded-lg border border-gold/40 bg-gold/10 text-gold hover:border-gold hover:bg-gold/20 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={row.status === 'active'
                    ? 'Apply to Existing Unassigned'
                    : 'Reactivate to use'}
                >
                  <Zap size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditing(row)}
                  disabled={patchMut.isPending}
                  data-testid={`outlet-mapping-edit-${row.id}`}
                  className="p-2 rounded-lg border border-white/15 bg-white/5 text-white/70 hover:border-gold/60 hover:text-white disabled:opacity-40"
                  title="Edit mapping"
                >
                  <Edit2 size={13} />
                </button>
                <button
                  type="button"
                  onClick={() =>
                    patchMut.mutate({
                      mappingId: row.id,
                      patch: { status: row.status === 'active' ? 'inactive' : 'active' },
                    })
                  }
                  disabled={patchMut.isPending}
                  data-testid={`outlet-mapping-toggle-${row.id}`}
                  className="p-2 rounded-lg border border-white/15 bg-white/5 text-white/70 hover:border-gold/60 hover:text-white disabled:opacity-40"
                  title={row.status === 'active' ? 'Deactivate mapping' : 'Reactivate mapping'}
                >
                  {row.status === 'active' ? <PowerOff size={13} /> : <Power size={13} />}
                </button>
                <button
                  type="button"
                  onClick={() => onViewHistory?.(row)}
                  data-testid={`outlet-mapping-history-${row.id}`}
                  className="px-3 py-1.5 rounded-lg text-xs text-white/70 hover:text-white underline-offset-4 hover:underline"
                >
                  History
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <AssignBranchModal
          mode="edit"
          initialOutlet={editing.original_detected_outlet}
          initialBranchId={editing.mapped_branch_id}
          normalizedOutlet={editing.normalized_detected_outlet}
          branches={branches}
          loadingBranches={branchesQ.isLoading}
          pending={patchMut.isPending}
          onClose={() => setEditing(null)}
          onSubmit={({ mappedBranchId }) => {
            patchMut.mutate({
              mappingId: editing.id,
              patch: { mapped_branch_id: mappedBranchId },
            });
          }}
        />
      ) : null}
      {applying ? (
        <ApplyToExistingModal
          mapping={applying}
          branches={branches}
          onClose={() => setApplying(null)}
          onApplied={() => {
            qc.invalidateQueries({ queryKey: ['owner-outlet-mappings-list'] });
          }}
        />
      ) : null}
    </SectionCard>
  );
}
